// =============================================
// AllInternationalBookings.js
// =============================================

import React, { useState, useEffect, useMemo, useRef } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  Spinner,
  FormGroup,
  Badge,
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
} from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import axios from "axios"
import apiURL from "url"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import { Link } from "react-router-dom"

// =============================================
// Main Component
// =============================================
const AllInternationalBookings = () => {
  document.title = "International Bookings"

  // ---------------------------------------------
  // States
  // ---------------------------------------------
  const [bookings, setBookings] = useState([])
  const [filtered, setFiltered] = useState([])
  const [loading, setLoading] = useState(false)

  const [filters, setFilters] = useState({
    tracking_id: "",
    sender: "",
    receiver: "",
    status: "",
    carrier: "",
  })

  const [filterOpen, setFilterOpen] = useState(false)

  const [selectedBooking, setSelectedBooking] = useState(null)
  const [viewModal, setViewModal] = useState(false)

  const [dropdownOpen, setDropdownOpen] = useState({})

  // Load status list
  const [statusList, setStatusList] = useState([])
  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const { data } = await axios.get(`${apiURL}/international/statuses`)
        if (data.status === "success") setStatusList(data.data)
      } catch (err) {
        console.error("Error loading statuses", err)
      }
    }
    loadStatuses()
  }, [])

  // ---------------------------------------------
  // Fetch all bookings (ONE REQUEST ONLY)
  // ---------------------------------------------
  const fetchBookings = async () => {
    try {
      setLoading(true)
      const { data } = await axios.get(`${apiURL}/international/bookings`)

      if (data.status === "success") {
        setBookings(data.data)
        setFiltered(data.data)
      }
    } catch (err) {
      toastr.error("Failed to fetch bookings")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBookings()
  }, [])

  // ---------------------------------------------
  // Filtering
  // ---------------------------------------------
  useEffect(() => {
    let f = bookings

    if (filters.tracking_id)
      f = f.filter(b =>
        b.tracking_id.toLowerCase().includes(filters.tracking_id.toLowerCase())
      )

    if (filters.sender)
      f = f.filter(b =>
        `${b.sender_first_name} ${b.sender_last_name}`
          .toLowerCase()
          .includes(filters.sender.toLowerCase())
      )

    if (filters.receiver)
      f = f.filter(b =>
        `${b.receiver_first_name} ${b.receiver_last_name}`
          .toLowerCase()
          .includes(filters.receiver.toLowerCase())
      )

    if (filters.status)
      f = f.filter(
        b => b.status_name.toLowerCase() === filters.status.toLowerCase()
      )

    if (filters.carrier)
      f = f.filter(b =>
        b.carrier_name.toLowerCase().includes(filters.carrier.toLowerCase())
      )

    setFiltered(f)
  }, [filters, bookings])

  // ---------------------------------------------
  // Helpers
  // ---------------------------------------------
  const toggleDropdown = id => {
    setDropdownOpen(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const openViewModal = booking => {
    setSelectedBooking(booking)
    setViewModal(true)
  }

  const getStatusColor = hex => {
    return hex || "secondary"
  }

  // ---------------------------------------------
  // TABLE COLUMNS
  // ---------------------------------------------
  const columns = useMemo(
    () => [
      {
        Header: "Sr #",
        accessor: "index",
        Cell: ({ row }) => row.index + 1,
        disableSortBy: true,
      },

      {
        Header: "Tracking ID",
        accessor: "tracking_id",
        Cell: ({ value, row }) => (
          <Link onClick={() => openViewModal(row.original)}>{value}</Link>
        ),
      },

      {
        Header: "Sender",
        accessor: "sender_first_name",
        Cell: ({ row }) => (
          <div>
            <b>
              {row.original.sender_first_name} {row.original.sender_last_name}
            </b>
            <br />
            <small>{row.original.sender_phone}</small>
          </div>
        ),
      },

      {
        Header: "Receiver",
        accessor: "receiver_first_name",
        Cell: ({ row }) => (
          <div>
            <b>
              {row.original.receiver_first_name}{" "}
              {row.original.receiver_last_name}
            </b>
            <br />
            <small>{row.original.receiver_phone}</small>
          </div>
        ),
      },

      {
        Header: "Carrier",
        accessor: "carrier_name",
      },

      {
        Header: "Weight",
        accessor: "weight",
        Cell: ({ value }) => `${value} kg`,
      },

      {
        Header: "Pieces",
        accessor: "pieces",
      },

      {
        Header: "Status",
        accessor: "status_name",
        Cell: ({ row }) => (
          <Badge
            style={{
              backgroundColor: row.original.status_color,
              padding: "5px 10px",
            }}
          >
            {row.original.status_name}
          </Badge>
        ),
      },

      {
        Header: "Created",
        accessor: "created_at",
        Cell: ({ value }) =>
          new Date(value).toLocaleString("en-PK", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          }),
      },

      {
        Header: "Actions",
        Cell: ({ row }) => {
          const id = row.original.id
          const isOpen = dropdownOpen[id]
          return (
            <div className="position-relative">
              <Button
                className="btn btn-sm d-flex align-items-center gap-1"
                color="success"
                onClick={() => toggleDropdown(id)}
              >
                Action
                <i className="bx bx-dots-vertical-rounded" />
              </Button>

              {isOpen && (
                <div
                  className="position-absolute bg-white shadow-sm border rounded p-2"
                  style={{
                    top: "30px",
                    right: "0",
                    zIndex: 999,
                    minWidth: "120px",
                  }}
                >
                  <Button
                    color="link"
                    className="text-primary w-100 text-start p-1"
                    onClick={() => {
                      openViewModal(row.original)
                      toggleDropdown(id)
                    }}
                  >
                    <i className="bx bx-show me-1"></i>View
                  </Button>
                </div>
              )}
            </div>
          )
        },
      },
    ],
    [dropdownOpen]
  )

  // ---------------------------------------------
  // RENDER
  // ---------------------------------------------
  return (
    <div className="page-content">
      <Container fluid>
        {/* PAGE HEADER */}
        <Row className="mb-4">
          <Col xs="12">
            <h4 className="fw-semibold mb-0">All International Bookings</h4>
          </Col>
        </Row>

        {/* FILTERS */}
        <Accordion
          open={filterOpen ? "1" : ""}
          toggle={() => setFilterOpen(!filterOpen)}
          className="mb-4"
        >
          <AccordionItem>
            <AccordionHeader targetId="1">
              <i className="bx bx-filter-alt me-2"></i> Filters
            </AccordionHeader>
            <AccordionBody accordionId="1">
              <Row className="g-3">
                <Col md={2}>
                  <Label>Tracking ID</Label>
                  <Input
                    type="text"
                    value={filters.tracking_id}
                    onChange={e =>
                      setFilters({ ...filters, tracking_id: e.target.value })
                    }
                  />
                </Col>

                <Col md={2}>
                  <Label>Sender</Label>
                  <Input
                    type="text"
                    value={filters.sender}
                    onChange={e =>
                      setFilters({ ...filters, sender: e.target.value })
                    }
                  />
                </Col>

                <Col md={2}>
                  <Label>Receiver</Label>
                  <Input
                    type="text"
                    value={filters.receiver}
                    onChange={e =>
                      setFilters({ ...filters, receiver: e.target.value })
                    }
                  />
                </Col>

                <Col md={2}>
                  <Label>Status</Label>
                  <Input
                    type="select"
                    value={filters.status}
                    onChange={e =>
                      setFilters({ ...filters, status: e.target.value })
                    }
                  >
                    <option value="">All</option>
                    {statusList.map(s => (
                      <option key={s.id} value={s.status_name}>
                        {s.status_name}
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col md={2}>
                  <Label>Carrier</Label>
                  <Input
                    type="text"
                    value={filters.carrier}
                    onChange={e =>
                      setFilters({ ...filters, carrier: e.target.value })
                    }
                  />
                </Col>

                <Col md={2} className="d-flex align-items-end">
                  <Button
                    color="secondary"
                    className="w-100"
                    onClick={() =>
                      setFilters({
                        tracking_id: "",
                        sender: "",
                        receiver: "",
                        status: "",
                        carrier: "",
                      })
                    }
                  >
                    Clear
                  </Button>
                </Col>
              </Row>
            </AccordionBody>
          </AccordionItem>
        </Accordion>

        {/* TABLE */}
        <Card>
          <CardBody>
            {loading ? (
              <div className="text-center p-4">
                <Spinner />
                <p className="text-muted">Loading...</p>
              </div>
            ) : (
              <TableContainer
                columns={columns}
                data={filtered}
                isGlobalFilter={true}
                customPageSize={10}
              />
            )}
          </CardBody>
        </Card>

        {/* ============================================
            MODAL VIEW (NO SECOND API CALL)
        ============================================ */}
        <Modal
          isOpen={viewModal}
          toggle={() => setViewModal(false)}
          size="lg"
          className="booking-preview-modal"
        >
          <ModalHeader
            toggle={() => setViewModal(false)}
            className="border-bottom pb-3"
          >
            <div className="d-flex align-items-center">
              <div className="bg-light-primary rounded-circle p-2 me-3">
                <i className="bx bx-package fs-4 text-primary"></i>
              </div>
              <div>
                <h5 className="fw-bold mb-0">Booking Preview</h5>
                <p className="text-muted small mb-0">
                  Tracking ID:{" "}
                  <span className="fw-semibold">
                    {selectedBooking?.tracking_id}
                  </span>
                </p>
              </div>
            </div>
          </ModalHeader>

          <ModalBody className="pb-1">
            {selectedBooking && (
              <div className="row g-4">
                {/* LEFT COLUMN - Contact Information */}
                <div className="col-md-6">
                  {/* SENDER CARD */}
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-light-primary border-0 py-3">
                      <h6 className="fw-bold mb-0 d-flex align-items-center">
                        <i className="bx bx-user-circle me-2"></i>
                        Sender Information
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="d-flex align-items-start mb-3">
                        <div className="bg-light rounded-circle p-2 me-3">
                          <i className="bx bx-user text-muted"></i>
                        </div>
                        <div>
                          <div className="text-muted small">Name</div>
                          <div className="fw-semibold">
                            {selectedBooking.sender_first_name}{" "}
                            {selectedBooking.sender_last_name}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-start mb-3">
                        <div className="bg-light rounded-circle p-2 me-3">
                          <i className="bx bx-phone text-muted"></i>
                        </div>
                        <div>
                          <div className="text-muted small">Phone</div>
                          <div className="fw-semibold">
                            {selectedBooking.sender_phone}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-start mb-3">
                        <div className="bg-light rounded-circle p-2 me-3">
                          <i className="bx bx-envelope text-muted"></i>
                        </div>
                        <div>
                          <div className="text-muted small">Email</div>
                          <div className="fw-semibold text-break">
                            {selectedBooking.sender_email}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-start">
                        <div className="bg-light rounded-circle p-2 me-3">
                          <i className="bx bx-map text-muted"></i>
                        </div>
                        <div>
                          <div className="text-muted small">Address</div>
                          <div className="fw-semibold">
                            {selectedBooking.sender_address}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT COLUMN - Receiver Information */}
                <div className="col-md-6">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-light-warning border-0 py-3">
                      <h6 className="fw-bold mb-0 d-flex align-items-center">
                        <i className="bx bx-user-check me-2"></i>
                        Receiver Information
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="d-flex align-items-start mb-3">
                        <div className="bg-light rounded-circle p-2 me-3">
                          <i className="bx bx-user text-muted"></i>
                        </div>
                        <div>
                          <div className="text-muted small">Name</div>
                          <div className="fw-semibold">
                            {selectedBooking.receiver_first_name}{" "}
                            {selectedBooking.receiver_last_name}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-start mb-3">
                        <div className="bg-light rounded-circle p-2 me-3">
                          <i className="bx bx-phone text-muted"></i>
                        </div>
                        <div>
                          <div className="text-muted small">Phone</div>
                          <div className="fw-semibold">
                            {selectedBooking.receiver_phone}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-start mb-3">
                        <div className="bg-light rounded-circle p-2 me-3">
                          <i className="bx bx-envelope text-muted"></i>
                        </div>
                        <div>
                          <div className="text-muted small">Email</div>
                          <div className="fw-semibold text-break">
                            {selectedBooking.receiver_email}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-start">
                        <div className="bg-light rounded-circle p-2 me-3">
                          <i className="bx bx-map text-muted"></i>
                        </div>
                        <div>
                          <div className="text-muted small">Address</div>
                          <div className="fw-semibold">
                            {selectedBooking.receiver_address}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* PACKAGE DETAILS - Full width */}
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-light-info border-0 py-3">
                      <h6 className="fw-bold mb-0 d-flex align-items-center">
                        <i className="bx bx-package me-2"></i>
                        Package Details
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-3 col-6 mb-3">
                          <div className="text-muted small">Package Type</div>
                          <div className="fw-semibold">
                            {selectedBooking.package_type}
                          </div>
                        </div>
                        <div className="col-md-3 col-6 mb-3">
                          <div className="text-muted small">Weight</div>
                          <div className="fw-semibold d-flex align-items-center">
                            <i className="bx bx-dumbbell me-1"></i>
                            {selectedBooking.weight} kg
                          </div>
                        </div>
                        <div className="col-md-3 col-6 mb-3">
                          <div className="text-muted small">Pieces</div>
                          <div className="fw-semibold d-flex align-items-center">
                            <i className="bx bx-layer me-1"></i>
                            {selectedBooking.pieces}
                          </div>
                        </div>
                        <div className="col-md-3 col-6 mb-3">
                          <div className="text-muted small">
                            Volumetric Weight
                          </div>
                          <div className="fw-semibold">
                            {selectedBooking.volumetric_weight}
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="text-muted small">Dimensions</div>
                          <div className="fw-semibold d-flex align-items-center">
                            <i className="bx bx-ruler me-2"></i>
                            {selectedBooking.length} × {selectedBooking.width} ×{" "}
                            {selectedBooking.height} cm
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CONTENTS - Full width */}
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-light-success border-0 py-3">
                      <h6 className="fw-bold mb-0 d-flex align-items-center">
                        <i className="bx bx-list-ul me-2"></i>
                        Contents ({selectedBooking.contents.length} items)
                      </h6>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-light">
                            <tr>
                              <th className="small fw-semibold">Product</th>
                              <th className="small fw-semibold">Quantity</th>
                              <th className="small fw-semibold">HS Code</th>
                              <th className="small fw-semibold">Weight</th>
                              <th className="small fw-semibold">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedBooking.contents.map((c, i) => (
                              <tr key={i}>
                                <td className="fw-semibold">{c.product}</td>
                                <td>
                                  <span className="badge bg-light-primary text-primary">
                                    {c.quantity} pcs
                                  </span>
                                </td>
                                <td>
                                  <code className="bg-light small">
                                    HS {c.hs_code}
                                  </code>
                                </td>
                                <td>{c.weight} kg</td>
                                <td>
                                  <span className="fw-semibold text-success">
                                    {c.declared_value} PKR
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* STATUS HISTORY - Full width with scroll */}
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-light-purple border-0 py-3">
                      <h6 className="fw-bold mb-0 d-flex align-items-center">
                        <i className="bx bx-history me-2"></i>
                        Status History ({selectedBooking.history.length}{" "}
                        updates)
                      </h6>
                    </div>
                    <div className="card-body p-0">
                      <div
                        className="timeline-container"
                        style={{ maxHeight: "250px", overflowY: "auto" }}
                      >
                        <div className="timeline px-3 py-2">
                          {selectedBooking.history.map((h, i) => (
                            <div key={i} className="timeline-item d-flex">
                              <div className="timeline-marker">
                                <div
                                  className="rounded-circle p-1"
                                  style={{
                                    backgroundColor: h.status_color,
                                    width: "12px",
                                    height: "12px",
                                    marginTop: "5px",
                                  }}
                                ></div>
                                {i !== selectedBooking.history.length - 1 && (
                                  <div className="timeline-line"></div>
                                )}
                              </div>
                              <div className="timeline-content ms-3 pb-3 flex-grow-1">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div>
                                    <Badge
                                      style={{
                                        backgroundColor: h.status_color,
                                        color: "#fff",
                                        border: "none",
                                        padding: "4px 10px",
                                        fontSize: "12px",
                                      }}
                                      className="mb-1"
                                    >
                                      {h.status_name}
                                    </Badge>
                                    {h.location && (
                                      <div className="small d-flex align-items-center mt-1">
                                        <i className="bx bx-map-pin me-1"></i>
                                        {h.location}
                                      </div>
                                    )}
                                    {h.description && (
                                      <div className="small text-muted mt-1">
                                        <i className="bx bx-message-rounded me-1"></i>
                                        {h.description}
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-end">
                                    <div className="small text-muted">
                                      <i className="bx bx-time me-1"></i>
                                      {new Date(
                                        h.created_at
                                      ).toLocaleDateString()}
                                    </div>
                                    <div className="small text-muted">
                                      {new Date(
                                        h.created_at
                                      ).toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </ModalBody>

          <ModalFooter className="border-0 pt-3">
            <Button
              color="light"
              onClick={() => setViewModal(false)}
              className="border px-4"
            >
              <i className="bx bx-x me-1"></i> Close
            </Button>
          </ModalFooter>
        </Modal>
        <style>{`
        
        .booking-preview-modal .modal-body {
  max-height: calc(90vh - 150px);
  overflow-y: auto;
}

.timeline-container {
  scrollbar-width: thin;
}

.timeline {
  position: relative;
}

.timeline-item {
  position: relative;
}

.timeline-marker {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.timeline-line {
  position: absolute;
  width: 2px;
  background-color: #e9ecef;
  top: 18px;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
}

.bg-light-purple {
  background-color: rgba(150, 122, 255, 0.1) !important;
}
        `}</style>
      </Container>
    </div>
  )
}

export default AllInternationalBookings
